import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import API from "../../api/api";

const DEFAULT_HOME_SECTIONS = [
  {
    id: "hero",
    type: "hero_banner",
    title: "",
    enabled: true,
    data_source: { endpoint: "/api/uploads/hero-banners/" },
  },
  {
    id: "promotions",
    type: "promotion_strip",
    title: "Offers for you",
    enabled: true,
    data_source: { endpoint: "/api/uploads/promotions/" },
  },
  {
    id: "categories",
    type: "category_grid",
    title: "Shop by Category",
    enabled: true,
    data_source: { endpoint: "/api/uploads/category-banners/?is_active=true" },
  },
  {
    id: "nearby_shops",
    type: "nearby_shops",
    title: "Nearby Shops",
    enabled: true,
    data_source: { endpoint: "/api/shops/nearby/", params: { radius_km: 5, limit: 20 } },
  },
];

export default function AdminHolidaySupportControls() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageRecord, setPageRecord] = useState(null);
  const [allSections, setAllSections] = useState(DEFAULT_HOME_SECTIONS);

  // Form State
  const [isActive, setIsActive] = useState(true);
  const [phone, setPhone] = useState("08068243903");
  const [whatsapp, setWhatsapp] = useState("918095918103");
  const [whatsappMessage, setWhatsappMessage] = useState(
    "Hi, I want to know more about Asiayapp Holiday Packages"
  );
  const [title, setTitle] = useState("Need help with your holiday booking?");
  const [subtitle, setSubtitle] = useState(
    "Speak with certified travel consultants for customized itineraries & group bookings."
  );

  const [snack, setSnack] = useState({ open: false, type: "success", msg: "" });

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      // 1. Check if an admin page config exists for ecommerce_home
      const res = await API.get("/ui/admin/pages/?key=ecommerce_home");
      const list = Array.isArray(res?.data?.results)
        ? res.data.results
        : Array.isArray(res?.data)
        ? res.data
        : [];
      const record = list.find((r) => r.key === "ecommerce_home") || list[0] || null;

      let sections = DEFAULT_HOME_SECTIONS;
      if (record && record.config && Array.isArray(record.config.sections)) {
        setPageRecord(record);
        sections = record.config.sections;
      } else {
        // Fallback: fetch current public sections
        try {
          const pub = await API.get("/ui/pages/ecommerce-home/");
          if (pub?.data?.sections && Array.isArray(pub.data.sections)) {
            sections = pub.data.sections;
          }
        } catch (_) {}
      }

      setAllSections(sections);

      // Check if holiday_support section exists in sections
      const sec = sections.find((s) => s.id === "holiday_support");
      if (sec) {
        setIsActive(sec.enabled !== false);
        if (sec.title) setTitle(sec.title);
        const params = sec.data_source?.params || {};
        if (params.phone !== undefined) setPhone(params.phone);
        if (params.whatsapp !== undefined) setWhatsapp(params.whatsapp);
        if (params.whatsapp_message !== undefined) setWhatsappMessage(params.whatsapp_message);
        if (params.subtitle !== undefined) setSubtitle(params.subtitle);
      }
    } catch (e) {
      setSnack({
        open: true,
        type: "error",
        msg: "Failed to load current configuration. Defaults loaded.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);

      const updatedSupportSection = {
        id: "holiday_support",
        type: "holiday_support_card",
        title: String(title).trim() || "Need help with your holiday booking?",
        enabled: Boolean(isActive),
        data_source: {
          endpoint: "/api/uploads/promotions/",
          params: {
            phone: String(phone).trim(),
            whatsapp: String(whatsapp).trim().replace(/\D/g, ""),
            whatsapp_message: String(whatsappMessage).trim(),
            subtitle: String(subtitle).trim(),
          },
        },
      };

      // Filter out any previous holiday_support section and append updated one
      const existingWithoutSupport = allSections.filter((s) => s.id !== "holiday_support");
      const finalSections = [...existingWithoutSupport, updatedSupportSection];

      if (pageRecord?.id) {
        // Update existing record
        const payload = {
          title: pageRecord.title || "Ecommerce Home",
          is_active: pageRecord.is_active !== false,
          version: (pageRecord.version || 1) + 1,
          config: { sections: finalSections },
        };
        const updateRes = await API.patch(`/ui/admin/pages/${pageRecord.id}/`, payload);
        setPageRecord(updateRes?.data || { ...pageRecord, ...payload });
      } else {
        // Create new record
        const payload = {
          key: "ecommerce_home",
          title: "Ecommerce Home",
          is_active: true,
          version: 1,
          config: { sections: finalSections },
        };
        const createRes = await API.post("/ui/admin/pages/", payload);
        setPageRecord(createRes?.data || null);
      }

      setAllSections(finalSections);
      setSnack({
        open: true,
        type: "success",
        msg: "Holiday booking support settings updated live!",
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to save settings.";
      setSnack({ open: true, type: "error", msg: String(msg) });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: "#ea580c",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(234, 88, 12, 0.3)",
              }}
            >
              <SupportAgentRoundedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a" }}>
                Holiday Support & Contact Controls
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Admin control for the Travel Consultant calling number, WhatsApp link, and visibility on the Home screen.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
          sx={{
            bgcolor: "#ea580c",
            "&:hover": { bgcolor: "#c2410c" },
            fontWeight: 800,
            borderRadius: "10px",
            px: 3,
            textTransform: "none",
          }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* Left Column: Form Settings */}
        <Grid item xs={12} md={7}>
          <Card elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", mb: 3 }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Card Visibility & Controls
              </Typography>

              {/* Active / Hidden Switch */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: "12px",
                  bgcolor: isActive ? "#f0fdf4" : "#fef2f2",
                  border: `1.5px solid ${isActive ? "#bbf7d0" : "#fecaca"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isActive ? (
                      <VisibilityRoundedIcon sx={{ color: "#16a34a", fontSize: 22 }} />
                    ) : (
                      <VisibilityOffRoundedIcon sx={{ color: "#dc2626", fontSize: 22 }} />
                    )}
                    <Typography sx={{ fontWeight: 800, color: isActive ? "#15803d" : "#991b1b" }}>
                      {isActive ? "Card is ACTIVE (Visible on Home Screen)" : "Card is HIDDEN (Disabled on Home Screen)"}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.5 }}>
                    {isActive
                      ? "The holiday booking support card will appear at the bottom of the holiday section for all consumers."
                      : "The support card will be completely removed from the user home screen."}
                  </Typography>
                </Box>
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="success"
                  sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#16a34a" } }}
                />
              </Paper>

              <Divider sx={{ my: 2.5 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Contact Numbers
              </Typography>

              <Stack spacing={2.5}>
                {/* Calling Phone Number */}
                <TextField
                  label="Calling Phone / Mobile Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. 08068243903 or 9876543210"
                  helperText="Tapping 'Call' on the home screen immediately dials this number."
                  fullWidth
                  InputProps={{
                    startAdornment: <PhoneInTalkRoundedIcon sx={{ color: "#ea580c", mr: 1, fontSize: 20 }} />,
                  }}
                />

                {/* WhatsApp Number */}
                <TextField
                  label="WhatsApp Number (with country code, no +)"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  placeholder="e.g. 918095918103"
                  helperText="Include country code (e.g. 91 for India). Do not include + or spaces."
                  fullWidth
                  InputProps={{
                    startAdornment: <ChatBubbleRoundedIcon sx={{ color: "#16a34a", mr: 1, fontSize: 20 }} />,
                  }}
                />

                {/* WhatsApp Pre-filled Message */}
                <TextField
                  label="WhatsApp Pre-filled Message"
                  value={whatsappMessage}
                  onChange={(e) => setWhatsappMessage(e.target.value)}
                  placeholder="Hi, I want to know more about Asiayapp Holiday Packages"
                  helperText="This message is pre-typed into the user's WhatsApp chat when they tap the button."
                  multiline
                  rows={2}
                  fullWidth
                />
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", mb: 2 }}>
                Headlines & Description
              </Typography>

              <Stack spacing={2.5}>
                <TextField
                  label="Card Title / Headline"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Need help with your holiday booking?"
                  fullWidth
                />

                <TextField
                  label="Card Subtitle / Description"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  placeholder="Speak with certified travel consultants for customized itineraries & group bookings."
                  multiline
                  rows={2}
                  fullWidth
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column: Live Mobile Preview */}
        <Grid item xs={12} md={5}>
          <Card elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#0f172a", mb: 0.5 }}>
                📱 Live Mobile Preview
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5 }}>
                Exact real-time preview of how this card looks on mobile phones:
              </Typography>

              {!isActive && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px", fontWeight: 700 }}>
                  ⚠️ This card is currently set to HIDDEN and will NOT show on the Home screen.
                </Alert>
              )}

              {/* Preview Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "18px",
                  background: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
                  border: "1.5px solid #fed7aa",
                  opacity: isActive ? 1 : 0.45,
                  transition: "opacity 200ms ease",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      bgcolor: "#ea580c",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 4px 12px rgba(234, 88, 12, 0.35)",
                      flexShrink: 0,
                    }}
                  >
                    <PhoneInTalkRoundedIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#9a3412", lineHeight: 1.3 }}>
                      {title || "Need help with your holiday booking?"}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#c2410c", fontWeight: 600, mt: 0.25, lineHeight: 1.3 }}>
                      {subtitle || "Speak with certified travel consultants for customized itineraries & group bookings."}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="small"
                    component="a"
                    href={`tel:${String(phone).replace(/\s+/g, "")}`}
                    startIcon={<PhoneInTalkRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      flex: 1,
                      bgcolor: "#ea580c",
                      "&:hover": { bgcolor: "#c2410c" },
                      color: "#ffffff",
                      fontWeight: 800,
                      fontSize: 12,
                      borderRadius: "8px",
                      textTransform: "none",
                      py: 0.75,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Call {phone || "08068243903"}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`https://wa.me/${String(whatsapp).replace(/\D/g, "")}?text=${encodeURIComponent(whatsappMessage || "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<ChatBubbleRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      flex: 1,
                      borderColor: "#ea580c",
                      color: "#c2410c",
                      fontWeight: 800,
                      fontSize: 12,
                      borderRadius: "8px",
                      textTransform: "none",
                      py: 0.75,
                      bgcolor: "#ffffff",
                      "&:hover": { bgcolor: "#fff7ed", borderColor: "#c2410c" },
                      whiteSpace: "nowrap",
                    }}
                  >
                    WhatsApp
                  </Button>
                </Stack>
              </Paper>

              {/* Status information */}
              <Box sx={{ mt: 3, p: 2, bgcolor: "#ffffff", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "#334155", display: "block", mb: 0.5 }}>
                  Active Endpoint Mapping:
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#64748b", display: "block" }}>
                  GET /api/ui/pages/ecommerce-home/
                </Typography>
                <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.5 }}>
                  Changes take effect immediately across all client devices without needing app re-install or code rebuild.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snackbar Notification */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.type}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%", borderRadius: "10px", fontWeight: 700 }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
